import { Button, Result } from "antd";
import { useState } from "react";
import { createBrowserRouter, Navigate, RouterProvider, useParams } from "react-router";
import AgentPortalHomePage from "./pages/AgentPortalHomePage";
import GlobalPortalLoginPage from "./pages/GlobalPortalLoginPage";
import PortalAssistantPage from "./pages/PortalAssistantPage";
import PortalEntryRedirectPage from "./pages/PortalEntryRedirectPage";
import PortalForgotPasswordPage from "./pages/PortalForgotPasswordPage";
import PortalSignupPage from "./pages/PortalSignupPage";
import PortalHomePage from "./pages/PortalHomePage";
import PortalItineraryBuilderPage from "./pages/PortalItineraryBuilderPage";
import PortalItineraryDetailPage from "./pages/PortalItineraryDetailPage";
import PortalItineraryListPage from "./pages/PortalItineraryListPage";
import PortalQuotesPage from "./pages/PortalQuotesPage";
import PortalTripRequestDetailPage from "./pages/PortalTripRequestDetailPage";
import PortalProfilePage from "./pages/PortalProfilePage";
import PortalResetPasswordPage from "./pages/PortalResetPasswordPage";
import PortalWorkspacePage from "./pages/PortalWorkspacePage";
import { useTenant } from "./tenant/TenantContext";

/** Displays the login experience appropriate to the resolved hostname. */
const PortalLoginPage = () => {
  const { tenantId } = useTenant();
  return tenantId ? <AgentPortalHomePage /> : <GlobalPortalLoginPage />;
};

/** Displays an isolated portal 404 response. */
const NoMatch = () => (
  <main className="grid min-h-dvh place-items-center bg-[#FBFBFB] p-6">
    <Result
      status="404"
      title="404"
      subTitle="The page you visited does not exist."
      extra={
        <Button type="primary" href="/">
          Back to portal
        </Button>
      }
    />
  </main>
);

/** Redirects older non-tenant workspace URLs to canonical portal routes. */
const LegacyWorkspaceRedirect = ({ target }: { target: "itineraries" | "new-itinerary" | "detail" }) => {
  const { id = "" } = useParams();
  const destination =
    target === "detail" && id
      ? `/itineraries/${id}`
      : target === "new-itinerary"
        ? "/new-itinerary"
        : "/itineraries";
  return <Navigate to={destination} replace />;
};

const createPortalRouter = () =>
  createBrowserRouter([
    { path: "/", element: <PortalEntryRedirectPage /> },
    { path: "/login", element: <PortalLoginPage /> },
    { path: "/signup", element: <PortalSignupPage /> },
    { path: "/forgot-password", element: <PortalForgotPasswordPage /> },
    { path: "/reset-password", element: <PortalResetPasswordPage /> },
    { path: "/workspace", element: <LegacyWorkspaceRedirect target="itineraries" /> },
    { path: "/workspace/itineraries", element: <LegacyWorkspaceRedirect target="itineraries" /> },
    { path: "/workspace/itineraries/new", element: <LegacyWorkspaceRedirect target="new-itinerary" /> },
    { path: "/workspace/itineraries/:id", element: <LegacyWorkspaceRedirect target="detail" /> },
    {
      element: <PortalWorkspacePage />,
      children: [
        { path: "/dashboard", element: <PortalHomePage /> },
        { path: "/assistant", element: <PortalAssistantPage /> },
        { path: "/assistant/:threadId", element: <PortalAssistantPage /> },
        { path: "/profile", element: <PortalProfilePage /> },
        { path: "/requests", element: <Navigate to="/itineraries" replace /> },
        { path: "/requests/new", element: <PortalItineraryBuilderPage /> },
        { path: "/requests/:leadId/options/new", element: <PortalItineraryBuilderPage /> },
        { path: "/requests/:leadId", element: <PortalTripRequestDetailPage /> },
        { path: "/itineraries", element: <PortalQuotesPage /> },
        { path: "/bookings", element: <PortalItineraryListPage key="bookings" mode="bookings" /> },
        { path: "/itineraries/:id", element: <PortalItineraryDetailPage /> },
        { path: "/itineraries/:id/edit", element: <PortalItineraryBuilderPage /> },
        { path: "/new-itinerary", element: <Navigate to="/requests/new" replace /> },
      ],
    },
    { path: "*", element: <NoMatch /> },
  ]);

/** Renders the standalone domain-aware agent portal application. */
export default function App() {
  const [router] = useState(createPortalRouter);
  return <RouterProvider router={router} />;
}
